<template>
	<article class="Filters">
		<SelectedFiltersSection :chips="activeFilterChips"/>
		<FilterSelectionAccordion
			v-for="section in filterSections"
			:key="section.title"
			:title="section.title"
			:selection="section.selection"
			:filterType="section.filterType"
			:filter-component="section.filterComponent"
		/>
	</article>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useSearchStore } from '@stores/useSearchStore';
import { FilterType } from '@domain/enums/FilterType';
import FilterSelectionAccordion from './FilterSelectionAccordion/FilterSelectionAccordion.vue';
import SelectedFiltersSection from './SelectedFiltersSection/SelectedFiltersSection.vue';
import { TuitionFeeFilterValue } from '@studyportals/search-api-interface';
import { storeToRefs } from 'pinia';
import { IChip } from '@app/interfaces/dtos/IChip';
import TuitionFee from './TuitionFee/TuitionFee.vue';
import DurationFilter from './DurationFilter/DurationFilter.vue';
import FormatFilter from './FormatFilter/FormatFilter.vue';
import AttendanceFilter from './AttendanceFilter/AttendanceFilter.vue';
import DegreeFilter from './DegreeFilter/DegreeFilter.vue';
import SpecialProgrammeFilter from './SpecialProgrammeFilter/SpecialProgrammeFilter.vue';
import OrganisationFilter from './OrganisationFilter/OrganisationFilter.vue';
import LocationFilter from './LocationFilter/LocationFilter.vue';
import DisciplineFilter from './DisciplineFilter/DisciplineFilter.vue';
import { formatCurrency } from '@app/utils/formatCurrency';

const searchStore = useSearchStore();
const { userNationality, userCurrency } = storeToRefs(searchStore);

const fieldOfStudiesText = computed(() => {
	return formatFilterValues(FilterType.DISCIPLINES);
});

const universityText = computed(() => formatFilterValues(FilterType.ORGANISATIONS));

const locationText = computed(() => {
	const parts = [
		formatFilterValues(FilterType.AREA),
		formatFilterValues(FilterType.COUNTRY),
		formatFilterValues(FilterType.CONTINENT)
	].filter(Boolean);

	return parts.join(', ');
});

const tuitionFeeText = computed(() => formatFilterValues(FilterType.TUITION_FEE));
const durationText = computed(() => formatFilterValues(FilterType.DURATION));
const formatText = computed(() => formatFilterValues(FilterType.DENSITIES));
const attendanceText = computed(() => formatFilterValues(FilterType.ATTENDANCE));
const degreeTypeText = computed(() => formatFilterValues(FilterType.DEGREE_TYPE));
const specialProgrammeText = computed(() => formatFilterValues(FilterType.SPECIAL_PROGRAMMES));

const userLocale = computed(() => userNationality.value || 'en-US');
const userCurr = computed(() => userCurrency!.value || 'USD');

const personalizedFilterValueResolvers: Partial<Record<FilterType, (value: string) => string | null>> = {
	[FilterType.TUITION_FEE]: (value) => formatCurrencyRange(value)
};

const formatFilterValues = (type: FilterType): string => {
	if (type === FilterType.TUITION_FEE) {
		return personalizedFilterValueResolvers[FilterType.TUITION_FEE]!(searchStore.filtersSelection
			.filter((filter) => filter.type === type)
			.flatMap((filter) => filter.values)[0]?.id ?? '') || '';
	}

	return searchStore.filtersSelection
		.filter((filter) => filter.type === type)
		.flatMap((filter) => filter.values)
		.map((value) => value.name)
		.join(', ');
};

const tuitionFeeLabels: Record<string, string> = {
    [TuitionFeeFilterValue.ZERO_TO_TWO_THOUSAND_FIVE_HUNDRED]: 'Affordable'
};

const formatCurrencyRange = (value: string): string => {
    if (tuitionFeeLabels[value]) return tuitionFeeLabels[value];

    try {
        const [min, max] = JSON.parse(value) as [number, number];
        const minValue = min === -1 ? null : formatCurrency(min, userLocale.value, userCurr.value);
        const maxValue = max === -1 ? null : formatCurrency(max, userLocale.value, userCurr.value);

        if (min === 0 && maxValue) return `Less than ${maxValue}`;
        if (minValue && maxValue) return `${minValue} - ${maxValue}`;
        if (minValue) return `${minValue}+`;
        if (maxValue) return `Up to ${maxValue}`;
    } catch {
        // fall back to raw value
    }
    return value;
};

const filterSections = computed(() => [
	{
		title: 'Field of Study',
		selection: fieldOfStudiesText.value,
		filterType: FilterType.DISCIPLINES,
		filterComponent: DisciplineFilter
	},
	{
		title: 'University',
		selection: universityText.value,
		filterType: FilterType.ORGANISATIONS,
		filterComponent: OrganisationFilter
	},
	{
		title: 'Location',
		selection: locationText.value,
		filterType: FilterType.COUNTRY,
		filterComponent: LocationFilter
	},
	{
		title: 'Tuition Fee',
		selection: tuitionFeeText.value,
		filterType: FilterType.TUITION_FEE,
		filterComponent: TuitionFee
	},
	{
		title: 'Duration',
		selection: durationText.value,
		filterType: FilterType.DURATION,
		filterComponent: DurationFilter
	},
	{
		title: 'Format',
		selection: formatText.value,
		filterType: FilterType.DENSITIES,
		filterComponent: FormatFilter
	},
	{
		title: 'Attendance',
		selection: attendanceText.value,
		filterType: FilterType.ATTENDANCE,
		filterComponent: AttendanceFilter
	},
	{
		title: 'Degree Type',
		selection: degreeTypeText.value,
		filterType: FilterType.DEGREE_TYPE,
		filterComponent: DegreeFilter
	},
	{
		title: 'Special Programme',
		selection: specialProgrammeText.value,
		filterType: FilterType.SPECIAL_PROGRAMMES,
		filterComponent: SpecialProgrammeFilter
	}
]);

const activeFilterChips = computed(() => {
	const withoutPortalTypeFilter = searchStore.filtersSelection.filter(
		(filter) => filter.type !== FilterType.EDUCATION_LEVEL
	);

	return withoutPortalTypeFilter.flatMap((filter) => {
		return filter.values.map((value) => {
			let label = value.name;
			if (personalizedFilterValueResolvers[filter.type]) {
				label = personalizedFilterValueResolvers[filter.type]!(value.id)!;
			}

			return {
				id: filter.type,
				value: value.id,
				label: label
			} as IChip;
		});
	});
});

</script>

<style scoped lang="scss">
	.Filters {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		width: 100%;

		@include Breakpoint(Large, ExtraLarge) {
			display: block;
			border-radius: 0.5rem;
			border: 1px solid $Grey-300;
			overflow: hidden;
		}
	}
</style>
